import type { Task } from '~/types/models';

export type SubtaskRollup = {
    /** Total number of direct subtasks */
    total: number;
    /** Number of subtasks with status 'Done' */
    completed: number;
    /** Completion percentage (0–100), 0 when there are no subtasks */
    completionPercent: number;
    /** True when at least one incomplete subtask has a past due date */
    hasOverdue: boolean;
};

/**
 * Compute a rollup summary of direct subtasks for a parent task.
 *
 * This is a pure, side-effect-free function – no Firestore reads or writes.
 * All derived values are calculated from the in-memory task list that is
 * already held by the task store / passed to the component.
 *
 * @param parentId - The `id` of the parent task.
 * @param tasks    - The full flat task array (root + subtasks).
 * @param today    - Optional ISO date string for the current day
 *                   (defaults to today; injectable for testing).
 * @returns A {@link SubtaskRollup} describing subtask health.
 */
export function getSubtaskRollup(
    parentId: string,
    tasks: Task[],
    today: string = new Date().toISOString().split('T')[0],
): SubtaskRollup {
    const children = tasks.filter(t => t.subtaskOfId === parentId);
    const total = children.length;

    if (total === 0) {
        return { total: 0, completed: 0, completionPercent: 0, hasOverdue: false };
    }

    const completed = children.filter(t => t.status === 'Done').length;
    const completionPercent = Math.round((completed / total) * 100);
    const hasOverdue = children.some(
        t => t.status !== 'Done' && !!t.dueDate && t.dueDate < today,
    );

    return { total, completed, completionPercent, hasOverdue };
}
